import { useQuery } from '@tanstack/react-query';
import { useUser } from '@dfl/react-security';
import { useCallback, useMemo } from 'react';
import BusinessService from 'modules/dashboard/services/business.service';
import {
  INTERNATIONAL_GATEWAYS,
  NATIONAL_GATEWAYS,
  supportedCurrencies,
  supportedPaymentGateways,
} from 'modules/common/constants';
import { ICurrencyPriceData, IPlan, IPriceData } from 'modules/subscriptions/interfaces/IPlan';
import { AVAILABLE_CAMPAIGN_TYPES } from 'modules/campaigns/constants';

export const CURRENT_SPACE_KEY = 'current-spaces';
export const useCurrentSpace = () => {
  const { user } = useUser();

  const INTERNATIONAL_SPACE_TYPES = (
    process.env.REACT_APP_INTERNATIONAL_SPACE_TYPES || 'FOREIGN_COMPANY,FOREIGN_INVESTMENT'
  )
    .split(',')
    .map((el) => el.trim());

  const { data, refetch, isLoading, ...rest } = useQuery(
    [CURRENT_SPACE_KEY],
    async () => {
      return await BusinessService.currentSpace();
    },
    { enabled: !!user },
  );

  const currentSpace = useMemo(() => {
    if (!data || isLoading) return null;
    if (!data.businessInformation) {
      return { ...data };
    }
    if (!data?.commercialName) {
      data.commercialName = data.name;
    }
    data.isOnboardingComplete = !!data.businessInformation.onBoardingCompleteDate;
    return { ...data };
  }, [data, isLoading]);

  const currencies = useMemo(() => {
    if (INTERNATIONAL_SPACE_TYPES.includes(currentSpace?.type)) {
      return supportedCurrencies?.filter((el) => ['USD', 'EUR'].includes(el));
    }
    return supportedCurrencies?.filter((el) => ['CUP'].includes(el));
  }, [currentSpace]);

  const internationalSpace = !!currentSpace?.type && INTERNATIONAL_SPACE_TYPES.includes(currentSpace?.type);

  const campaignTypes = useMemo(() => {
    return AVAILABLE_CAMPAIGN_TYPES.map((el) => el.trim());
  }, []);

  const paymentGateways = useMemo(() => {
    if (INTERNATIONAL_SPACE_TYPES.includes(currentSpace?.type)) {
      return supportedPaymentGateways?.filter((el) => INTERNATIONAL_GATEWAYS.includes(el));
    }
    return supportedPaymentGateways?.filter((el) => NATIONAL_GATEWAYS.includes(el));
  }, [currentSpace]);

  const getPlanPrice = useCallback(
    (plan: IPlan) => {
      const currency = currencies?.find((c) => !!plan?.prices?.[c]);
      if (currency) {
        return {
          ...(plan?.prices?.[currency] as IPriceData),
          currency,
        } satisfies ICurrencyPriceData;
      }
      return undefined;
    },
    [currencies],
  );

  return {
    currentSpace,
    currencies,
    paymentGateways,
    getPlanPrice,
    campaignTypes,
    refetch,
    isLoading,
    internationalSpace,
    ...rest,
  };
};
